import { Fragment } from "react";
import Link from "next/link";

/**
 * Les trois documents juridiques, sur une ligne.
 *
 * # Pourquoi un composant et pas trois liens écrits sur place
 *
 * Ils étaient déjà écrits sur place, à quatre endroits, et aucun des quatre ne
 * portait les trois : les réglages du Studio avaient la confidentialité et les
 * mentions, l'écran de compte les conditions et la confidentialité, la
 * bibliothèque les mentions seules. Personne n'avait décidé ça, c'est
 * simplement ce qu'on ajoute quand on ajoute un lien à l'endroit où on est.
 *
 * Le même argument que `app-header-nav.ts` fait pour la navigation : une liste
 * lue depuis plusieurs surfaces se dérive d'un modèle, sinon elle diverge, et
 * le jour où elle diverge c'est sur la surface qu'on n'aura pas relue.
 *
 * # Pourquoi ça compte plus qu'avant
 *
 * L'application est embarquée dans une coque iOS. La politique de
 * confidentialité doit y être atteignable sans compte et depuis n'importe quel
 * écran, et les conditions générales aussi puisqu'on y importe des fichiers.
 * Aucun des quatre points d'entrée d'origine ne tenait cette promesse : trois
 * dépendaient d'un geste en cours (créer un compte, importer, consulter le
 * répertoire) et le quatrième du Studio.
 *
 * # `Link` et non `<a href>`
 *
 * Les quatre surfaces d'origine faisaient un chargement complet. Dans la coque
 * iOS, ça vide le Studio monté en permanence et redemande les samples, pour
 * afficher trois paragraphes. La navigation cliente garde tout en place.
 *
 * Sa contrepartie est `onNavigate` : la page légale se rend sous le calque
 * appelant (elle est en `z-10`, les réglages et les modales sont bien plus
 * haut), donc la surface qui porte ces liens doit se refermer en même temps.
 * Un chargement complet masquait le problème en emportant le calque avec lui.
 */

/** Les trois routes légales. Le libellé plein est celui des pages elles-mêmes,
 *  le court sert aux surfaces étroites (menu du compte, sheet de nav) où les
 *  trois libellés pleins font ~300 px et passent à la ligne sur un « · ». */
const LEGAL_LINKS = [
  { href: "/conditions", full: "Conditions générales", short: "CGU" },
  { href: "/confidentialite", full: "Confidentialité", short: "Confidentialité" },
  { href: "/mentions", full: "Mentions légales", short: "Mentions" },
] as const;

type LegalLinksProps = {
  /**
   * Refermer la surface appelante. Facultatif seulement pour celles qui n'ont
   * rien à refermer, c'est-à-dire aucune pour l'instant.
   */
  onNavigate?: () => void;
  /** `short` sous ~250 px de largeur utile. */
  labels?: "full" | "short";
  /**
   * La taille et la couleur viennent de l'appelant : ces liens sont un pied de
   * surface, ils prennent le gris de celle qui les héberge plutôt que d'imposer
   * le leur à quatre habillages différents. D'où `color: inherit` sur les liens
   * eux-mêmes.
   */
  className?: string;
  /** Les réglages du Studio peignent avec `SET`, une palette locale en styles
   *  en ligne et non en classes utilitaires. Sans ça, cette surface-là devrait
   *  envelopper le composant dans un `div` dont le seul rôle serait de porter
   *  une couleur. */
  style?: React.CSSProperties;
};

export function LegalLinks({
  onNavigate,
  labels = "full",
  className,
  style,
}: LegalLinksProps) {
  return (
    <p className={className} style={style}>
      {LEGAL_LINKS.map((link, index) => (
        <Fragment key={link.href}>
          {/* Le séparateur est décoratif : lu à voix haute entre deux liens,
              « point médian » n'apporte rien à qui tabule dans la liste. */}
          {index > 0 ? <span aria-hidden>{" · "}</span> : null}
          <LegalLink
            href={link.href}
            onNavigate={onNavigate}
            label={labels === "short" ? link.short : link.full}
          />
        </Fragment>
      ))}
    </p>
  );
}

/**
 * Un lien légal seul, dans une phrase.
 *
 * Il existe parce que certaines surfaces ne citent qu'un document, et le citent
 * au fil du texte : « En savoir plus » sous le commutateur de statistiques,
 * l'attribution du corpus en pied de bibliothèque. Le pied de surface à trois
 * liens n'a rien à y faire, mais la mécanique du lien, elle, est la même : la
 * navigation cliente et la fermeture du calque appelant.
 */
export function LegalLink({
  href,
  label,
  onNavigate,
  className,
  style,
}: {
  href: (typeof LEGAL_LINKS)[number]["href"];
  label: string;
  onNavigate?: () => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <Link
      href={href}
      onClick={onNavigate}
      /* Ces trois pages sont en `force-dynamic` et se lisent depuis l'API :
         précharger les trois à chaque ouverture du menu du compte ferait trois
         requêtes pour des liens que presque personne ne suit. Le cache de cinq
         minutes de `serverGet` amortirait, mais amortir un travail inutile
         reste du travail inutile. */
      prefetch={false}
      className={`underline underline-offset-2 transition-opacity duration-150 hover:opacity-70 ${className ?? ""}`}
      style={{ color: "inherit", ...style }}
    >
      {label}
    </Link>
  );
}
